import { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGetServiceProvider } from 'src/use-cases/serviceProvider/useServiceProvider';
import { FormContainer, useForm } from 'react-hook-form-mui';
import { useHasPermission } from 'src/use-cases/user/useUserPermissions';
import { PermissionType } from 'src/types/permission';
import { featureFlags } from 'src/config/featureFlags';
import { useUserContext } from 'src/use-cases/user/userDetails';
import { useChartReports } from '../../organisms/analytics/useAnalytics';
import { LandingPerformance } from '../../organisms/landing/Performance';
import { LandingTopWidget, LandingTopContent, LandingMenu, LandingAudit, LandingAnalytics, LandingRateCard, LandingSectionName } from '../../organisms/landing';
import { LayoutSidebar } from '../../organisms/sidebar/LayoutSidebar';

export const LandingPage = () => {
  const [tab, setTab] = useState<string>(LandingSectionName.ALL);
  const location = useLocation();
  const navigate = useNavigate();

  const { data, isLoading: isLoadingProviders, refetch, isFetching } = useGetServiceProvider();
  const { userDetails } = useUserContext();

  const { data: analyticsData } = useChartReports();
  const [analyticsCount, setAnalyticsCount] = useState(0);

  const providers = data?.data || [];
  const [performanceCount, setPerformanceCount] = useState(0);

  // Check permissions for each section
  const { hasPermission: hasAuditPermission } = useHasPermission(PermissionType.AUDIT);
  const { hasPermission: hasPerformancePermission } = useHasPermission(PermissionType.PERFORMANCE);
  const { hasPermission: hasReportPermission } = useHasPermission(PermissionType.REPORT);
  // Assuming Rate Card has the same permission as AUDIT or use a specific permission if available
  const { hasPermission: hasRateCardPermission } = useHasPermission(PermissionType.AUDIT);

  // Determine which sections to display based on permissions and selected tab
  const isRootWithSidebar = featureFlags.SHOW_SIDEBAR_NAV && location.pathname === '/' && !location.search;

  // When on landing with sidebar, show section based on URL params
  // When on landing without sidebar, show section based on tab selection
  const sectionParam = new URLSearchParams(location.search).get('section');

  // Handle display logic - either via URL parameter when using sidebar or via tab when not using sidebar
  const isDisplayAudit =
    hasAuditPermission &&
    ((isRootWithSidebar && sectionParam === LandingSectionName.AUDIT) ||
      (!isRootWithSidebar && (tab === LandingSectionName.ALL || tab === LandingSectionName.AUDIT)));

  const isDisplayPerformance =
    hasPerformancePermission &&
    ((isRootWithSidebar && sectionParam === LandingSectionName.PERFORMANCE) ||
      (!isRootWithSidebar && (tab === LandingSectionName.ALL || tab === LandingSectionName.PERFORMANCE)));

  const isDisplayAnalytics =
    hasReportPermission &&
    ((isRootWithSidebar && sectionParam === LandingSectionName.ANALYTIC) ||
      (!isRootWithSidebar && (tab === LandingSectionName.ALL || tab === LandingSectionName.ANALYTIC)));

  const isDisplayRateCard =
    hasRateCardPermission &&
    ((isRootWithSidebar && sectionParam === LandingSectionName.RATECARD) ||
      (!isRootWithSidebar && (tab === LandingSectionName.ALL || tab === LandingSectionName.RATECARD)));
  // Create filtered menu items based on permissions
  const availableSections = useMemo(() => {
    const sections = [];

    // When using sidebar, don't create menu tabs
    if (featureFlags.SHOW_SIDEBAR_NAV && location.pathname === '/') {
      return sections;
    }

    // Standard behavior when not using sidebar nav or not on root page

    // Always include ALL
    sections.push(LandingSectionName.ALL);

    // Add other sections based on permissions
    if (hasAuditPermission) sections.push(LandingSectionName.AUDIT);
    if (hasPerformancePermission) sections.push(LandingSectionName.PERFORMANCE);
    if (hasReportPermission) sections.push(LandingSectionName.ANALYTIC);
    if (hasRateCardPermission) sections.push(LandingSectionName.RATECARD);

    return sections;
  }, [hasAuditPermission, hasPerformancePermission, hasReportPermission, hasRateCardPermission]);

  // Read section from URL query parameters and handle default section
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sectionParam = params.get('section');

    // If no section parameter is provided and we're on the root page, redirect to AUDIT section by default
    if (!sectionParam && location.pathname === '/' && hasAuditPermission) {
      // Set a small delay to avoid immediate redirect during initial load
      setTimeout(() => {
        navigate('/?section=AUDIT', { replace: true });
      }, 100);
      return;
    }

    if (sectionParam && Object.values(LandingSectionName).includes(sectionParam as LandingSectionName)) {
      // Only set the tab if the user has permission for this section
      if (
        (sectionParam === LandingSectionName.AUDIT && hasAuditPermission) ||
        (sectionParam === LandingSectionName.PERFORMANCE && hasPerformancePermission) ||
        (sectionParam === LandingSectionName.ANALYTIC && hasReportPermission) ||
        (sectionParam === LandingSectionName.RATECARD && hasRateCardPermission) ||
        sectionParam === LandingSectionName.ALL
      ) {
        setTab(sectionParam);
      }
    }
  }, [location.search, location.pathname, hasAuditPermission, hasPerformancePermission, hasReportPermission, hasRateCardPermission, navigate]);

  // Set default tab to first available section if current tab is not available
  useEffect(() => {
    if (availableSections.length > 0 && !availableSections.includes(tab)) {
      setTab(availableSections[0]);
    }
  }, [availableSections, tab]);

  useEffect(() => {
    if (analyticsData) {
      setAnalyticsCount(analyticsData.filter((item) => item.type === 'REPORT_ANALYTIC').length);
      setPerformanceCount(analyticsData.filter((item) => item.type === 'PERFORMANCE_ASSESSMENT').length);
    }
  }, [analyticsData]);

  const handleChange = (newValue: string) => {
    // Don't trigger a change if we're already on this tab
    if (tab === newValue) return;

    // Update tab state and URL
    setTab(newValue);
    navigate(`/${newValue === LandingSectionName.ALL ? '' : `?section=${newValue}`}`, { replace: true });
  };

  const formContext = useForm({
    defaultValues: {
      auditSort: 'ACTION_NEEDED',
      analyticSort: 'desc',
      performanceSort: 'desc',
      ratecardSort: 'desc',
    },
  });

  return (
    <FormContainer formContext={formContext}>
      <div className='relative bg-neutral-10'>
        <LandingTopWidget />
        <LayoutSidebar>
          <>
            {featureFlags.SHOW_SIDEBAR_NAV ? (
              <div className='pt-48 pb-24 text-center'>
                <h1 className='text-[22px] leading-[34px] xl:leading-[46px] xl:text-30 font-semibold mb-4 text-neutral-80'>
                  Hi {userDetails?.firstName} {userDetails?.lastName},
                </h1>
                <h2 className='text-18 xl:text-20 leading-30 xl:leading-32 text-neutral-80'>
                  I’m Freya — {userDetails?.displayRole === 'Procurement Manager' && <>protecting your negotiated value, one invoice at a time.</>}
                  {(userDetails?.displayRole === 'Logistics Manager' || userDetails?.displayRole === 'Supply Chain') && (
                    <>
                      making sure your transportation network stays <br /> optimized, accurate, and under control.
                    </>
                  )}
                  {userDetails?.displayRole === 'Finance' && (
                    <>
                      locking in compliance with <br />
                      every invoice, every charge, every audit trail, automatically.
                    </>
                  )}
                  {userDetails?.displayRole === 'Management' && <>because the best decisions start with data you can actually trust.</>}
                </h2>
              </div>
            ) : (
              <>
                <LandingTopContent />
                <LandingMenu
                  onChangeMenu={handleChange}
                  selected={tab}
                  auditTotal={hasAuditPermission ? providers?.length || 0 : 0}
                  analyticTotal={hasReportPermission ? analyticsCount : 0}
                  performanceTotal={hasPerformancePermission ? performanceCount : 0}
                  ratecardTotal={hasRateCardPermission ? providers?.length || 0 : 0}
                  availableSections={availableSections}
                  onRefresh={refetch}
                />
              </>
            )}

            <div
              className={`pt-32 pb-[40px] xl:pt-40 xl:pb-64 flex flex-col w-full ${
                !featureFlags.SHOW_SIDEBAR_NAV ? 'max-w-[90rem] mx-auto px-3%' : 'px-24 xl:px-32'
              }`}
            >
              {isDisplayAudit && <LandingAudit providers={providers} isLoading={isLoadingProviders || isFetching} onRefresh={refetch} />}
              {isDisplayPerformance && <LandingPerformance />}
              {isDisplayAnalytics && <LandingAnalytics />}
              {isDisplayRateCard && <LandingRateCard />}
            </div>
          </>
        </LayoutSidebar>
      </div>
    </FormContainer>
  );
};
